<template>
  <div class="route route-container">
    <!-- 发件卡片 -->
    <div class="stop route-stop--sender">
      <span class="label">
        <span class="dot" />
        {{ $t('trajectory.senderInfo') }}
      </span>
      <span class="main stop-main">{{ senderLocationText }}</span>
      <span class="sub stop-sub">{{ $t('trajectory.zipCode') }} {{ senderZipcode || '--' }}</span>
    </div>

    <!-- 中间：小货车 + 运单状态 -->
    <div class="mid route-mid">
      <span class="truck truck-icon" :style="truckStyle">
        <svg
          fill="none"
          height="18"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.4"
          viewBox="0 0 22 22"
          width="18"
        >
          <path d="M2 15V7a1 1 0 0 1 1-1h9v9M12 9h4l3 3v3h-2.5" />
          <circle cx="6.5" cy="16" r="1.6" />
          <circle cx="15.5" cy="16" r="1.6" />
          <path d="M8.1 16h5.8M2 16h2.9" />
        </svg>
      </span>
      <span class="status status-text" :style="statusStyle">{{ currentStatusText }}</span>
    </div>

    <!-- 收件卡片 -->
    <div class="stop route-stop--receiver" style="text-align: right;">
      <span class="label" style="justify-content: flex-end;">
        <span class="dot" />
        {{ $t('trajectory.receiverInfo') }}
      </span>
      <span class="main stop-main">{{ receiverLocationText }}</span>
      <span class="sub stop-sub">{{ $t('trajectory.zipCode') }} {{ receiverZipcode || '--' }}</span>
    </div>
  </div>
</template>

<script>
/**
 * 轨迹查询 - 顶部发收件路由概览卡片组件
 * 展示发件地（城市/州/邮编）、收件地（城市/州/邮编）及中间运单当前状态徽标与运输动效
 */
export default {
  name: 'TrackRouteHeader',
  props: {
    /**
     * 路由概览数据对象
     * @property {string} senderCity 发件城市
     * @property {string} senderState 发件州代码（如 SP、MG）
     * @property {string} senderZipcode 发件人邮编
     * @property {string} receiveCity 收件城市
     * @property {string} receiveState 收件州代码
     * @property {string} receiveZipcode 收件人邮编
     * @property {string} status 运单状态名称（多语言）
     * @property {string|number} statusCode 运单状态编码或数字代码
     */
    routeInfo: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    /**
     * 格式化发件地址：城市 / 州
     */
    senderLocationText() {
      const city = this.routeInfo.senderCity ? String(this.routeInfo.senderCity).trim() : '';
      const state = this.routeInfo.senderState ? String(this.routeInfo.senderState).trim() : '';
      if (city && state) {
        return `${city} / ${state}`;
      }
      return city || state || '--';
    },
    /**
     * 发件人邮政编码
     */
    senderZipcode() {
      return this.routeInfo.senderZipcode ? String(this.routeInfo.senderZipcode).trim() : '';
    },
    /**
     * 格式化收件地址：城市 / 州
     */
    receiverLocationText() {
      const city = this.routeInfo.receiveCity ? String(this.routeInfo.receiveCity).trim() : '';
      const state = this.routeInfo.receiveState ? String(this.routeInfo.receiveState).trim() : '';
      if (city && state) {
        return `${city} / ${state}`;
      }
      return city || state || '--';
    },
    /**
     * 收件人邮政编码
     */
    receiverZipcode() {
      return this.routeInfo.receiveZipcode ? String(this.routeInfo.receiveZipcode).trim() : '';
    },
    /**
     * 运单当前状态展示文本
     */
    currentStatusText() {
      const text = this.routeInfo.status || this.routeInfo.waybillStatusName;
      return (text && String(text).trim()) ? String(text).trim() : '--';
    },
    /**
     * 根据运单状态代码及关键词匹配状态机主题配色（包含前景色与背景浅色）
     */
    statusTheme() {
      const code = (this.routeInfo.statusCode || '').toLowerCase();
      const statusText = (this.currentStatusText || '').toLowerCase();

      // 1. 已签收（绿色系）
      if (code === 'signed' || statusText.indexOf('签收') > -1 || statusText.indexOf('entreg') > -1) {
        return { color: '#00B578', bg: '#E8FFF3' };
      }
      // 2. 异常件/投递失败（橙色系）
      if (code.indexOf('problem') > -1 || code.indexOf('fail') > -1 || statusText.indexOf('异常') > -1 || statusText.indexOf('失败') > -1 || statusText.indexOf('problema') > -1) {
        return { color: '#FF7D00', bg: '#FFF7E8' };
      }
      // 3. 派件中/干线运输中（蓝色系）
      if (code === 'delivering' || code.indexOf('transit') > -1 || statusText.indexOf('派件') > -1 || statusText.indexOf('运输') > -1 || statusText.indexOf('entrega') > -1) {
        return { color: '#165DFF', bg: '#E8F3FF' };
      }
      // 4. 仓储中转/入库（青色系）
      if (code.indexOf('storage') > -1 || statusText.indexOf('仓') > -1 || statusText.indexOf('armazém') > -1) {
        return { color: '#14C9C9', bg: '#E8F7F8' };
      }
      // 5. 默认安骏品牌绿
      return { color: '#00B578', bg: '#E8FFF3' };
    },
    /**
     * 中间小货车图标徽标动态样式
     */
    truckStyle() {
      return {
        background: this.statusTheme.bg,
        color: this.statusTheme.color
      };
    },
    /**
     * 中间状态文字颜色样式
     */
    statusStyle() {
      return {
        color: this.statusTheme.color
      };
    }
  }
};
</script>

<style scoped>
.route {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 16px;
  padding: 16px 0;
  align-items: center;
  box-sizing: border-box;
}

.route .mid {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.route .mid .truck {
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s ease;
}

.route .mid .status {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.stop {
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: #F7F8FA;
  min-width: 0;
}

.stop .label {
  font-size: 12px;
  color: #86909C;
  display: flex;
  align-items: center;
  gap: 6px;
}

.stop .label .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #86909C;
  flex-shrink: 0;
}

.stop .main {
  font-size: 14px;
  font-weight: 600;
  color: #1D2129;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stop .sub {
  font-size: 12px;
  color: #4E5969;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
